/**
 * Ships a browser-side failure to the observability service.
 *
 * The browser never holds the ingest secret — it posts to our own same-origin
 * `/api/client-error`, and api-gateway forwards it with the secret server-side.
 *
 * Every call is best-effort and silent: a reporter that throws while reporting
 * an error is worse than no reporter at all.
 */
const ENDPOINT = '/api/client-error'
const MAX_PER_SESSION = 20
/** A message repeated inside this window is the same event, not a new one. */
const REPEAT_WINDOW_MS = 30_000

let sent = 0
const lastSeen = new Map<string, number>()

/**
 * The connected wallet, registered once and attached to every report.
 *
 * Without it a report says a failure happened and not to whom — and the whole
 * point of recording these is that the person who hit one will not explain it.
 * They send a screenshot. The wallet is what turns that screenshot into
 * something we can look up.
 */
let reportingWallet: string | undefined

export function setReportingWallet(wallet: string | null | undefined): void {
  reportingWallet = wallet ? wallet.toLowerCase() : undefined
}

export interface ClientErrorReport {
  message: string
  stack?: string
  route?: string
  /**
   * 'transaction' covers errors the app CAUGHT and showed the user — the ones
   * no global handler ever sees. They were invisible to observability until a
   * user hit a wallet-provider failure, saw a wall of raw text in a toast, and
   * nobody could reconstruct it afterwards.
   */
  /** `wallet` covers connecting: a failure there leaves the person unable to
   *  reach anything, and used to leave us nothing at all. */
  /** 'toast' is every error the app decided to show a person. */
  kind?: 'render' | 'window' | 'promise' | 'transaction' | 'wallet' | 'toast'
  wallet?: string
  /** False when the app had no specific message for it — a gap worth seeing. */
  handled?: boolean
}

/**
 * A wallet rejection is a decision, not a fault.
 *
 * The swap path knew this and dropped them; the global handlers did not, so
 * "User rejected the request." arrived as an unhandled promise and sat in the
 * issue list next to real failures. Recording somebody pressing Cancel buries
 * the signal this reporter exists to carry.
 */
const USER_DECLINED =
  /(user rejected|user denied|action_rejected|rejected the request|user cancell?ed|request rejected by user)/i

/** Exported so a test can be honest about the line without a browser. */
export function isUserDeclined(message: string): boolean {
  return USER_DECLINED.test(message)
}

export function reportClientError(report: ClientErrorReport): void {
  try {
    if (sent >= MAX_PER_SESSION) return
    if (isUserDeclined(report.message)) return

    // A user who presses a failing button four times produced one problem,
    // not four. Collapsing them keeps the session budget for real variety.
    const now = Date.now()
    const key = `${report.kind}:${report.message}`
    const previous = lastSeen.get(key)
    if (previous !== undefined && now - previous < REPEAT_WINDOW_MS) return
    lastSeen.set(key, now)

    sent += 1

    const body = JSON.stringify({
      ...report,
      wallet: report.wallet ?? reportingWallet,
      route: report.route ?? window.location.pathname,
      href: window.location.href,
      userAgent: navigator.userAgent.slice(0, 300),
      /*
       * Which build the browser was actually running.
       *
       * Without it, an error seen after a fix is unanswerable: a stale tab and
       * an unfixed path look identical in the log. That is not hypothetical —
       * the stake signer error reappeared six hours after its fix shipped,
       * from a wallet that had hit it before, and there was no way to tell
       * which of the two it was. One field, and the next time it is a fact.
       */
      build: __BUILD_ID__,
      ts: new Date().toISOString()
    })

    // sendBeacon survives the page being torn down, which is exactly when the
    // interesting errors happen. fetch is the fallback where it is unavailable.
    if (navigator.sendBeacon?.(ENDPOINT, new Blob([body], { type: 'application/json' }))) {
      return
    }
    void fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body,
      keepalive: true
    }).catch(() => undefined)
  } catch {
    // Never let error reporting become the error.
  }
}

/**
 * A page left open across a deploy asks for a chunk that no longer exists.
 *
 * Vercel answers the missing file with index.html, so the browser says
 * "'text/html' is not a valid JavaScript MIME type" or "Failed to fetch
 * dynamically imported module" — and the feature behind that chunk simply does
 * not open, with no explanation. Seen three times this week, one of them on
 * the connect flow, which is the worst place to lose somebody.
 *
 * One reload fixes it, and only one is allowed: the flag is set before
 * reloading, so a genuinely missing chunk cannot become a loop.
 */
const STALE_BUILD_RELOADED = 'mprolab:stale-build-reloaded'
const STALE_CHUNK =
  /(failed to fetch dynamically imported module|error loading dynamically imported module|is not a valid javascript mime type|importing a module script failed)/i

export function isStaleChunkError(message: string): boolean {
  return STALE_CHUNK.test(message)
}

/** Returns true when the page is being reloaded because its build is gone. */
function reloadOnceForStaleBuild(message: string): boolean {
  if (!isStaleChunkError(message)) return false
  try {
    if (sessionStorage.getItem(STALE_BUILD_RELOADED)) return false
    sessionStorage.setItem(STALE_BUILD_RELOADED, String(Date.now()))
  } catch {
    return false // no session storage, no guard against a loop: do not reload
  }
  reportClientError({ message: `stale build, reloading: ${message.slice(0, 200)}`, kind: 'window', handled: true })
  window.location.reload()
  return true
}

/** Catches what React cannot see: errors outside render, and rejected promises. */
export function installGlobalErrorReporting(): void {
  if (typeof window === 'undefined') return
  window.addEventListener('error', (event) => {
    const message = String(event.message ?? 'window error')
    /*
     * "Script error." with no stack is what a browser says about a failure
     * inside a cross-origin script — a wallet extension's injected provider,
     * in practice. It carries no message, no file, no line, and can never be
     * acted on; it arrived five times beside the report that did name the
     * failure, doubling the queue for nothing.
     */
    if (/^script error\.?$/i.test(message.trim()) && !event.error?.stack) return
    if (reloadOnceForStaleBuild(message)) return
    reportClientError({
      kind: 'window',
      message: message.slice(0, 500),
      stack: event.error?.stack ? String(event.error.stack).split('\n').slice(0, 6).join('\n') : undefined
    })
  })
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason
    const text = String(reason?.message ?? reason ?? '')
    if (reloadOnceForStaleBuild(text)) return
    reportClientError({
      kind: 'promise',
      message: String(reason?.message ?? reason ?? 'unhandled rejection').slice(0, 500),
      stack: reason?.stack ? String(reason.stack).split('\n').slice(0, 6).join('\n') : undefined
    })
  })
}
